<script setup>
import { ref, computed } from 'vue'
import { usePrestationStore } from '../../stores/prestationStore'

const prestationStore = usePrestationStore()

// Form state
const selectedBeneficiaireId = ref('')
const isSubmitting = ref(false)

// Computed
const isModalVisible = computed(() => prestationStore.beneficiaireModal.show)
const currentBeneficiaire = computed(() => prestationStore.beneficiaireModal.currentBeneficiaire)
const paidChange = computed(() => currentBeneficiaire.value?.change?.locked === true)
const prestationId = computed(() => prestationStore.beneficiaireModal.prestationId)

// Methods
const closeModal = () => {
  prestationStore.closeBeneficiaireModal()
  selectedBeneficiaireId.value = ''
}

const handleSubmit = async () => {
  if (!selectedBeneficiaireId.value) {
    return
  }

  isSubmitting.value = true
  try {
    if (paidChange.value) {
      const result = await prestationStore.requestBeneficiaryChange(prestationId.value, selectedBeneficiaireId.value)
      window.location.assign(result.payment_url)
      return
    }
    await prestationStore.updateBeneficiaire(prestationId.value, selectedBeneficiaireId.value)
    closeModal()
  } catch (error) {
    // TODO: Error notification
    console.error('Erreur lors de la modification:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <!-- Modal Modifier Bénéficiaire -->
  <div v-if="isModalVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{ paidChange ? $t('Demander un changement de bénéficiaire') : $t('Modifier le bénéficiaire') }}</h3>
        <button type="button" class="modal-close" @click="closeModal">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="beneficiaire-form">
          <p class="current-beneficiaire">
            Bénéficiaire actuel : <strong>{{ currentBeneficiaire?.fullname || 'Non défini' }}</strong>
          </p>

          <p v-if="paidChange" role="note">{{ $t('Modification du bénéficiaire : des frais de 49 € s’appliquent.') }} {{ $t('Le bénéficiaire actuel est conservé jusqu’au règlement et à la facturation.') }}</p>
          <!-- Formulaire de sélection -->
          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="beneficiaire-select" class="form-label">
                Nouveau bénéficiaire :
              </label>
              <select 
                id="beneficiaire-select"
                v-model="selectedBeneficiaireId"
                class="form-select"
                :disabled="prestationStore.loading.beneficiaires || isSubmitting"
                required
              >
                <option value="">-- Sélectionner un bénéficiaire --</option>
                <option 
                  v-for="beneficiaire in prestationStore.availableBeneficiaires" 
                  :key="beneficiaire.id"
                  :value="beneficiaire.id"
                >
                  {{ beneficiaire.first_name }} {{ beneficiaire.last_name }} ({{ beneficiaire.email }})
                </option>
              </select>
              
              <!-- Loading state -->
              <div v-if="prestationStore.loading.beneficiaires" class="loading-text">
                <i class="bi bi-arrow-repeat spin"></i>
                Chargement des bénéficiaires...
              </div>
              
              <!-- Empty state -->
              <div v-else-if="prestationStore.availableBeneficiaires.length === 0" class="empty-text">
                <i class="bi bi-info-circle"></i>
                Aucun bénéficiaire disponible pour le moment
              </div>

              <div v-else class="mt-4 text-center"> 
                <a href="/customer/account/beneficiaires">Ajouter un bénéficiaire</a>
              </div>
            </div>

            <!-- Error message -->
            <div v-if="prestationStore.error" class="error-message">
              <i class="bi bi-exclamation-triangle"></i>
              {{ prestationStore.error }}
            </div>
          </form>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          type="button" 
          class="btn btn-secondary" 
          @click="closeModal"
          :disabled="isSubmitting"
        >
          Annuler
        </button>
        <button 
          type="button" 
          class="btn btn-primary" 
          @click="handleSubmit"
          :disabled="!selectedBeneficiaireId || isSubmitting || prestationStore.loading.beneficiaires"
        >
          <i v-if="isSubmitting" class="bi bi-arrow-repeat spin me-1"></i>
          {{ isSubmitting ? $t('Enregistrement...') : (paidChange ? $t('Continuer vers le règlement de 49 €') : $t('Enregistrer')) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  color: #6c757d;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
}

.current-beneficiaire {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.loading-text,
.empty-text {
  margin-top: 0.5rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-text {
  background-color: #eff6ff;
  color: #1e40af;
}

.empty-text {
  background-color: #fef3c7;
  color: #92400e;
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5c636a;
}

.btn-primary {
  background-color: #0d6efd;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0b5ed7;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.me-1 {
  margin-right: 0.25rem;
}
</style> 